import { BASE_GRID_OPTIONS, PoGrid } from '@/components/grid/PoGrid';
import { FunctionComponent, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  CellClickedEvent,
  GridApi,
  GridOptions,
  ICellRendererParams,
  RowDoubleClickedEvent,
  SortChangedEvent,
  isColumnSelectionCol,
} from 'ag-grid-community';
import { checkboxSelectionColumnDef } from '@/lib/ag-grid/selection';
import LinkCellRenderer, { LinkCellRendererParams } from '@/components/grid/cells/LinkCellRenderer';
import ErrorLoadingDataAlert from '@/components/feedback/ErrorLoadingDataAlert';
import { ColDefOrGroup } from '@/lib/ag-grid/types';
import { LocationAssetModel } from '../../types/LocationAssetModel';
import { RelativeDateCellRenderer } from '@/components/grid/cells/RelativeDateCellRenderer';
import { ActionBar } from '@/modules/application';
import { Button } from '@mui/material';
import { MoveAssetsToLocationModal } from '../MoveAssetsToLocationModal';
import { ColumnID } from '@/components/grid/column-ids';
import { PageSortOption, PageSortOrder } from '@/lib/api/pagination.page.dto';
import { LocationAssetSortOption } from '../../api/locations/location.contracts';
import { useTranslation } from '@/lib';
import ChangeAssetTypeModal from '../ChangeAssetTypeModal';
import { ActionBarDeleteButton } from '@/modules/application/components/ActionBarDeleteButton';
import { assetService } from '@/modules/assets/api/assets/asset.service';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

interface AssetsAtLocationGridProps {
  data?: LocationAssetModel[];
  isError: boolean;
  noBorder?: boolean;
  isLoading?: boolean;
  onAssetsMoved: () => void;
  onSortChange: (sortOption?: PageSortOption, sortDirection?: PageSortOrder) => void;
  selectedRows: LocationAssetModel[];
  onSelectedRowsChange: (selectedRows: LocationAssetModel[]) => void;
  sortOption?: PageSortOption;
  sortDirection?: PageSortOrder;
  onExportToExcelClicked: (gridApi: GridApi<LocationAssetModel> | undefined) => void;
}

export const AssetsAtLocationGrid: FunctionComponent<AssetsAtLocationGridProps> = ({
  data,
  isError,
  noBorder,
  isLoading,
  onAssetsMoved,
  onSortChange,
  selectedRows,
  onSelectedRowsChange,
  sortOption,
  sortDirection,
  onExportToExcelClicked,
}) => {
  const navigate = useNavigate();
  const [isMoveAssetModalOpen, setIsMoveAssetModalOpen] = useState(false);
  const [isChangeAssetTypeModalOpen, setIsChangeAssetTypeModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [gridApi, setGridApi] = useState<GridApi<LocationAssetModel> | null>(null);
  const { t } = useTranslation();

  const onCellClicked = useCallback((params: CellClickedEvent<LocationAssetModel>) => {
    if (isColumnSelectionCol(params.column)) {
      const node = params.node;
      node.setSelected(!node.isSelected());
    }
  }, []);

  // When the component gets re rendered we need to select the rows that were specified in the prop
  // for the clearing of the selection when searching
  if (gridApi) {
    const nodes = gridApi.getRenderedNodes();
    if (nodes.length) {
      for (const node of nodes) {
        if (selectedRows.find((sr) => (node.data ? sr.dto.assetId === node.data.dto.assetId : false))) {
          node.setSelected(true);
        } else {
          node.setSelected(false);
        }
      }
    }
  }

  const onSortChanged = (event: SortChangedEvent) => {
    if (event.columns && event.columns.length > 0 && event.source === 'uiColumnSorted') {
      const changedColumn = event.columns.find((col) => col.getSort());
      if (!changedColumn) {
        onSortChange();
        return;
      }
      const changedColumnId = changedColumn.getColId();
      let sortOption: LocationAssetSortOption | undefined = undefined;

      switch (changedColumnId) {
        case ColumnID.ASSET_CODE:
          sortOption = LocationAssetSortOption.ASSET_CODE;
          break;
        case ColumnID.ASSET_TYPE_NAME:
          sortOption = LocationAssetSortOption.ASSET_TYPE_NAME;
          break;
        case ColumnID.DATE_ARRIVED_AT_LOCATION:
          sortOption = LocationAssetSortOption.ARRIVED_AT;
          break;
        case ColumnID.STAYTIME:
          sortOption = LocationAssetSortOption.STAY_TIME;
          break;
        default:
          break;
      }

      let sortOrder: PageSortOrder | undefined = PageSortOrder.ASC;

      switch (changedColumn.getSort()) {
        case 'asc':
          sortOrder = PageSortOrder.ASC;
          break;
        case 'desc':
          sortOrder = PageSortOrder.DESC;
          break;
      }

      onSortChange(sortOption, sortOrder);
    }
  };

  const customGridOptions: GridOptions<LocationAssetModel> = {
    ...BASE_GRID_OPTIONS,
    getRowId: (params) => params.data.dto.assetId.toString(),
    onRowDoubleClicked(event: RowDoubleClickedEvent<LocationAssetModel, unknown>) {
      navigate(`/app/assets/${event.data?.dto.assetId}`);
    },
    rowSelection: { mode: 'multiRow' },
    selectionColumnDef: checkboxSelectionColumnDef(),
    onCellClicked: (event) => {
      onCellClicked(event);
    },
    onSelectionChanged: (event) => {
      onSelectedRowsChange(event.api.getSelectedRows());
    },
    getContextMenuItems: (params) => [
      'copy',
      'separator',
      {
        name: t('export_excel'),
        action: () => onExportToExcelClicked(params.api),
      },
    ],
    onGridReady: (event) => {
      setGridApi(event.api);

      // Set initial sort state to grid
      if (sortOption && sortDirection) {
        // Convert sort option to column id
        let columnId: ColumnID | undefined = ColumnID.FIRST_EVENT_DATE;
        switch (sortOption) {
          case LocationAssetSortOption.ARRIVED_AT:
            columnId = ColumnID.DATE_ARRIVED_AT_LOCATION;
            break;
          case LocationAssetSortOption.ASSET_TYPE_NAME:
            columnId = ColumnID.ASSET_TYPE_NAME;
            break;
          case LocationAssetSortOption.ASSET_CODE:
            columnId = ColumnID.ASSET_CODE;
            break;
          case LocationAssetSortOption.STAY_TIME:
            columnId = ColumnID.STAYTIME;
            break;
          default:
            break;
        }

        event.api.applyColumnState({
          state: [
            {
              colId: columnId,
              sort: sortDirection === PageSortOrder.ASC ? 'asc' : 'desc',
            },
          ],
          defaultState: { sort: null },
        });
      }
    },

    onSortChanged: onSortChanged,
  };

  const columns: ColDefOrGroup<LocationAssetModel>[] = [
    

    {
      colId: ColumnID.ASSET_CODE,
      field: 'dto.assetCode',
      headerName: t('asset'),
      // Render as link
      cellRenderer: LinkCellRenderer,
      cellRendererParams: (params: ICellRendererParams<LocationAssetModel>): LinkCellRendererParams => ({
        pathname: params.data?.dto.assetId ? `/app/assets/${params.data?.dto.assetId}` : undefined,
      }),
    },
    {
      colId: ColumnID.ASSET_TYPE_NAME,
      field: 'dto.assetTypeName',
      headerName: t('type'),
      // Render as link
      cellRenderer: LinkCellRenderer,
      cellRendererParams: (params: ICellRendererParams<LocationAssetModel>): LinkCellRendererParams => ({
        pathname: params.data?.dto.assetTypeId ? `/app/configuration/assets/types/${params.data?.dto.assetTypeId}` : undefined,
      }),
    },
    {
      colId: ColumnID.DATE_ARRIVED_AT_LOCATION,
      field: 'dto.dateArrivedAtLocation',
      headerName: t('arrived_at'),
      cellRenderer: RelativeDateCellRenderer,
      flex: 1,
    },
    {
      colId: ColumnID.STAYTIME,
      field: 'dto.dateArrivedAtLocation',
      headerName: t('stay_time'),
      cellRenderer: (params: ICellRendererParams<LocationAssetModel>) => {
        if (!params.value) return '';
        const arrivedAt = dayjs(params.value);
        const stayTime = dayjs().diff(arrivedAt, 'days');
        return t('number_of_days', { day: stayTime });
      },
      flex: 1,
    },
  ];

  function onMoveSelectedAssetsClicked() {
    setIsMoveAssetModalOpen(true);
  }

  function onMoveSelectedAssetsClosed() {
    setIsMoveAssetModalOpen(false);
    clearSelection();
  }

  function onChangeAssetTypeClicked() {
    setIsChangeAssetTypeModalOpen(true);
  }

  function onChangeAssetTypeClosed() {
    setIsChangeAssetTypeModalOpen(false);
    clearSelection();
  }

  function onAssetTypeChangedByGrid() {
    onAssetsMoved();
  }

  async function onDeleteSelectedAssets() {
    setIsDeleting(true);
    try {
      const response = await assetService.bulkDelete({ assetIds: selectedRows.map((lam) => lam.dto.assetId) });

      if (response.isSuccess) {
        toast.success(t('asset_deleted'));
        onAssetsMoved();
        clearSelection();
      } else {
        toast.error(t('error_deleting_asset'));
      }
    } finally {
      setIsDeleting(false);
    }
  }

  function clearSelection() {
    if (gridApi) {
      gridApi.deselectAll();
    }
    onSelectedRowsChange([]);
  }

  function onAssetsMovedByGrid() {
    onAssetsMoved();
  }

  return (
    <div className="flex h-full flex-1 grow flex-col">
      {isError ? (
        <ErrorLoadingDataAlert />
      ) : (
        <PoGrid
          noBorder={noBorder}
          isLoading={isLoading}
          colDefs={columns}
          rowData={data}
          gridOptions={customGridOptions}
          disableDefaultGridOptions
        />
      )}

      <ActionBar visible={selectedRows.length > 0}>
        <Button variant="outlined" color="primary" onClick={clearSelection}>
          {t('clear_selection')}
        </Button>
        <Button variant="contained" onClick={onMoveSelectedAssetsClicked}>
          {t('move_assets')}
        </Button>
        <Button variant="contained" onClick={onChangeAssetTypeClicked}>
          {t('change_asset_type')}
        </Button>
        <ActionBarDeleteButton
          isVisible
          isLoading={isDeleting}
          deleteConfirmationQuestion={t('delete_confirmation_question', { count: selectedRows.length })}
          onDeleteConfirmed={onDeleteSelectedAssets}
        />
      </ActionBar>
      <MoveAssetsToLocationModal
        selectedAssetIds={selectedRows.map((lam) => lam.dto.assetId)}
        isOpen={isMoveAssetModalOpen}
        onClose={onMoveSelectedAssetsClosed}
        onAssetsMoved={onAssetsMovedByGrid}
      />
      <ChangeAssetTypeModal
        selectedAssetIds={selectedRows.map((lam) => lam.dto.assetId)}
        isOpen={isChangeAssetTypeModalOpen}
        onClose={onChangeAssetTypeClosed}
        onAssetsMoved={onAssetTypeChangedByGrid}
      />
    </div>
  );
};
